import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Ban, Camera, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Loading } from "@/components/Loading";
import { useConfirm } from "@/components/ConfirmDialog";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ApiError } from "@/lib/apiClient";
import { readFormDraft } from "@/lib/formDraft";
import { mergeDraftValues, useFormDraftAutosave } from "@/hooks/useFormDraftAutosave";
import {
  publicPartnerForm,
  type PartnerFormPrefill,
} from "@/features/onboarding/api/onboarding.api";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Spinner } from "@/components/ui/spinner";
import { TextField, SelectField, DateField, FormFieldSize, FormRequiredCue } from "@/components/form";
import {
  ScanStatus, StepHeading, splitGivenNames, trimmed, useFilePreview,
  ConversionErrorBanner, conversionMessage, failedDocSlots, requiredDocSlots, REUPLOAD_HINT, type DocSlot,
  DraftSaveIndicator, DraftRestoredNotice, onboardingDraftKey, previousFileNote,
  applyScan, scanLabels, SCAN_KEPT_MESSAGE, isRetryableScanError, type ScannedField,
  DRAFT_SENSITIVE_FIELDS, type DraftFiles,
} from "@/features/onboarding/components/wizard";
import { downscaleImage } from "@/utils/image";
import { docSizeError, DOC_ACCEPT, withDocMimeType } from "@/utils/docFile";
import { fileDropTone, useFileDrop } from "@/hooks/useFileDrop";
import { BusyIndicator } from "@/components/BusyIndicator";
import { digitsOnly, formatSsn, formatEin, formatPhone, formatRouting, formatZip, isAbaRouting, isEin, isPhone10, isRouting9, isSsn9, isZip } from "@/utils/format";
import { OWNERSHIP_TYPES, canonicalOwnershipType, isSoleProprietor } from "@/features/onboarding/components/wizard/constants";
import { STATE_OPTIONS, stateCode } from "@/utils/states";
import { useZipAutoFill } from "@/hooks/useZipAutoFill";
import { toDateInput } from "@/utils/dates";
import { FIELD_MAX, tooLong } from "@/lib/fieldLimits";

const schema = z
  .object({
    firstName: z.string().trim().min(1, "Required.").max(FIELD_MAX.partnerForm.firstName, tooLong("First name", FIELD_MAX.partnerForm.firstName)),
    middleName: z.string().trim().max(FIELD_MAX.partnerForm.middleName, tooLong("Middle name", FIELD_MAX.partnerForm.middleName)),
    lastName: z.string().trim().min(1, "Required.").max(FIELD_MAX.partnerForm.lastName, tooLong("Last name", FIELD_MAX.partnerForm.lastName)),
    dateOfBirth: z.string().trim().min(1, "Required.").max(FIELD_MAX.partnerForm.dateOfBirth, tooLong("Date of birth", FIELD_MAX.partnerForm.dateOfBirth)),
    ownershipType: z.string().min(1, "Required.").max(FIELD_MAX.partnerForm.ownershipType, tooLong("Entity type", FIELD_MAX.partnerForm.ownershipType)),
    ssn: z.string(),
    ein: z.string(),
    legalName: z.string().max(FIELD_MAX.partnerForm.legalName, tooLong("Legal business name", FIELD_MAX.partnerForm.legalName)),
    email: z.string().trim().min(1, "Required.").email("Enter a valid email.").max(FIELD_MAX.partnerForm.email, tooLong("Email", FIELD_MAX.partnerForm.email)),
    phone: z.string().trim().min(1, "Required.").max(FIELD_MAX.partnerForm.phone, tooLong("Phone", FIELD_MAX.partnerForm.phone))
      .refine(isPhone10, "Enter a valid 10-digit phone number."),
    addressLine1: z.string().trim().min(1, "Required.").max(FIELD_MAX.partnerForm.addressLine1, tooLong("Business address", FIELD_MAX.partnerForm.addressLine1)),
    zip: z.string().trim().min(1, "Required.").max(FIELD_MAX.partnerForm.zip, tooLong("ZIP", FIELD_MAX.partnerForm.zip))
      .refine(isZip, "ZIP must be 5 or 9 digits."),
    state: z.string().min(1, "Required.").max(FIELD_MAX.partnerForm.state, tooLong("State", FIELD_MAX.partnerForm.state)),
    city: z.string().trim().min(1, "Required.").max(FIELD_MAX.partnerForm.city, tooLong("City", FIELD_MAX.partnerForm.city)),
    bankRoutingNumber: z.string().trim().min(1, "Required.")
      .refine(isRouting9, "Routing number must be 9 digits.")
      .refine((v) => !isRouting9(v) || isAbaRouting(v), "Enter a valid ABA routing number."),
    bankAccountNumber: z.string().trim().min(1, "Required.")
      .refine((v) => { const d = v.replace(/\D/g, ""); return d.length >= 4 && d.length <= 17; },
        "Bank account number must be 4 to 17 digits."),
  })
  .superRefine((v, ctx) => {
    if (isSoleProprietor(v.ownershipType)) {
      if (!v.ssn.trim()) ctx.addIssue({ code: "custom", path: ["ssn"], message: "Required." });
      else if (!isSsn9(v.ssn)) ctx.addIssue({ code: "custom", path: ["ssn"], message: "SSN must be 9 digits." });
    } else {
      if (!v.ein.trim()) ctx.addIssue({ code: "custom", path: ["ein"], message: "Required." });
      else if (!isEin(v.ein)) ctx.addIssue({ code: "custom", path: ["ein"], message: "EIN must be 9 digits." });
      if (!v.legalName.trim()) ctx.addIssue({ code: "custom", path: ["legalName"], message: "Required." });
    }
  });

type Fields = z.infer<typeof schema>;

const EMPTY: Fields = {
  firstName: "", middleName: "", lastName: "", dateOfBirth: "", ownershipType: "", ssn: "", ein: "", legalName: "",
  email: "", phone: "", addressLine1: "", zip: "", state: "", city: "",
  bankRoutingNumber: "", bankAccountNumber: "",
};

type LinkState = "checking" | "valid" | "invalid" | "expired";

interface DocOnFile {
  onFile: boolean;
  name: string | null;
}

type DocKey = "license" | "check";

interface ScanState {
  busy: boolean;
  msg: string | null;
  kind: "info" | "success" | "error";
  retry: File | null;
}

const IDLE_SCAN: ScanState = { busy: false, msg: null, kind: "info", retry: null };

const MISSING_LICENSE = "Please attach a photo of your driver's license.";
const MISSING_CHECK = "Please attach a photo of your voided check.";

function scanErrorMessage(e: unknown, doc: string, fallbackAction: string): string {
  return e instanceof ApiError && e.status === 400
    ? `We couldn't process that file. Upload a clearer photo or PDF of the ${doc}, or ${fallbackAction}.`
    : `Something went wrong reading your ${doc}. Please ${fallbackAction}.`;
}

export default function PartnerPublicPage() {
  const { token = "" } = useParams<{ token: string }>();
  const confirm = useConfirm();
  const draftKey = onboardingDraftKey("partner", token);
  const [initialDraft] = useState(() => readFormDraft<Fields>(draftKey));
  const [restoredAt, setRestoredAt] = useState<number | null>(initialDraft?.lastEditAt ?? null);
  const [previousFiles, setPreviousFiles] = useState<DraftFiles>(initialDraft?.files ?? {});
  const prefillValues = useRef<Fields>(EMPTY);

  const [linkState, setLinkState] = useState<LinkState>("checking");
  const [prefilled, setPrefilled] = useState(false);
  const [licenseOnFile, setLicenseOnFile] = useState<DocOnFile>({ onFile: false, name: null });
  const [checkOnFile, setCheckOnFile] = useState<DocOnFile>({ onFile: false, name: null });

  const [license, setLicense] = useState<File | null>(null);
  const [check, setCheck] = useState<File | null>(null);
  const [scan, setScan] = useState<Record<DocKey, ScanState>>({ license: IDLE_SCAN, check: IDLE_SCAN });
  const [failedDocs, setFailedDocs] = useState<DocSlot[]>([]);
  const [failAttempt, setFailAttempt] = useState(0);
  const [missingDocs, setMissingDocs] = useState<DocSlot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ emailAlreadyInUse: boolean } | null>(null);

  const hydratedZip = useRef<string | null>(null);
  const scanOnlyEmpty = useRef<Record<DocKey, boolean>>({ license: false, check: false });

  const form = useForm<Fields>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: EMPTY,
  });

  useEffect(() => {
    let cancelled = false;
    publicPartnerForm.prefill(token)
      .then((r) => {
        if (cancelled) return;
        if (!r?.valid) {
          setLinkState(r?.expired ? "expired" : "invalid");
          return;
        }
        setLinkState("valid");
        const onFile = toFields(r);
        prefillValues.current = onFile;
        const values = initialDraft ? mergeDraftValues(onFile, initialDraft.values) : onFile;
        hydratedZip.current = values.zip || null;
        setPrefilled(hasAnyValue(onFile));
        setLicenseOnFile({
          onFile: !!(r.currentForm?.hasDriverLicense || r.user?.hasDriverLicense),
          name: r.currentForm?.driverLicenseName ?? r.user?.driverLicenseName ?? null,
        });
        setCheckOnFile({
          onFile: !!(r.currentForm?.hasVoidCheck || r.user?.hasVoidCheck),
          name: r.currentForm?.voidCheckName ?? r.user?.voidCheckName ?? null,
        });
        form.reset(values);
      })
      .catch(() => { if (!cancelled) setLinkState("invalid"); });
    return () => { cancelled = true; };
  }, [token, form]);

  const draftFiles = useMemo<DraftFiles>(() => ({
    driverLicense: license?.name ?? previousFiles.driverLicense,
    voidedCheck: check?.name ?? previousFiles.voidedCheck,
  }), [license, check, previousFiles]);

  const autosave = useFormDraftAutosave({
    form,
    storageKey: draftKey,
    enabled: linkState === "valid" && !done,
    restored: initialDraft,
    step: 0,
    maxValidated: 0,
    files: draftFiles,
    sensitive: DRAFT_SENSITIVE_FIELDS,
  });

  function attachLicense(f: File | null) {
    setLicense(f);
    setPreviousFiles((p) => ({ ...p, driverLicense: undefined }));
  }
  function attachCheck(f: File | null) {
    setCheck(f);
    setPreviousFiles((p) => ({ ...p, voidedCheck: undefined }));
  }

  async function startOver() {
    const ok = await confirm({
      title: "Start over?",
      description: "Everything you've changed on this device will be cleared and the form goes back to what we have on file. This can't be undone.",
      confirmLabel: "Start over",
      destructive: true,
    });
    if (!ok) return;
    autosave.discard();
    setRestoredAt(null);
    setPreviousFiles({});
    setLicense(null);
    setCheck(null);
    setScan({ license: IDLE_SCAN, check: IDLE_SCAN });
    setFailedDocs([]);
    setMissingDocs([]);
    setError(null);
    hydratedZip.current = prefillValues.current.zip || null;
    form.reset(prefillValues.current);
  }

  useZipAutoFill(form.watch("zip"), (st, ci) => {
    if (form.getValues("zip") === hydratedZip.current) return;
    const sv = { shouldValidate: form.formState.isSubmitted };
    if (st) form.setValue("state", st, sv);
    if (ci) form.setValue("city", ci, sv);
  });

  const ownershipType = form.watch("ownershipType");
  const isSoleProp = isSoleProprietor(ownershipType);

  const ownershipOptions = useMemo(() => {
    const extra = ownershipType && !OWNERSHIP_TYPES.includes(ownershipType) ? [ownershipType] : [];
    return [...OWNERSHIP_TYPES, ...extra].map((o) => [o, o] as [string, string]);
  }, [ownershipType]);

  function updateScan(key: DocKey, next: Partial<ScanState>) {
    setScan((prev) => ({ ...prev, [key]: { ...prev[key], ...next } }));
  }

  function removeDoc(key: DocKey, set: (f: File | null) => void) {
    set(null);
    updateScan(key, IDLE_SCAN);
  }

  async function startScan(key: DocKey, f: File, set: (f: File | null) => void): Promise<File | null> {
    const sizeError = docSizeError(f);
    if (sizeError) {
      updateScan(key, { busy: false, kind: "error", msg: sizeError, retry: null });
      return null;
    }
    updateScan(key, { busy: true, kind: "info", msg: null, retry: null });
    const prepared = await downscaleImage(f);
    set(prepared);
    return prepared;
  }

  async function pickLicense(f: File) {
    scanOnlyEmpty.current.license = !!previousFiles.driverLicense && !licenseOnFile.onFile;
    const prepared = await startScan("license", f, attachLicense);
    if (prepared) await readLicense(prepared);
  }

  async function readLicense(prepared: File) {
    updateScan("license", { busy: true, kind: "info", msg: null, retry: null });
    const sv = { shouldValidate: form.formState.isSubmitted, shouldDirty: true };
    try {
      const d = await publicPartnerForm.scanDriverLicense(prepared);
      const { first, middle } = splitGivenNames(d.firstName, d.middleName);
      const values: ScannedField<Fields>[] = [
        ["firstName", first, "first name"],
        ["middleName", middle, "middle name"],
        ["lastName", trimmed(d.lastName), "last name"],
        ["dateOfBirth", toDateInput(d.dateOfBirth), "date of birth"],
        ["addressLine1", trimmed(d.addressLine1), "address"],
        ["zip", formatZip(trimmed(d.zip)), "ZIP"],
        ["state", stateCode(trimmed(d.state)), "state"],
        ["city", trimmed(d.city), "city"],
      ];
      const { read, applied } = applyScan(form, values, { ...sv, onlyEmpty: scanOnlyEmpty.current.license });
      if (read.length === 0) {
        updateScan("license", {
          kind: "error",
          msg: "We couldn't read anything off that file. Please fill in the fields below yourself.",
        });
        return;
      }
      const zip = applied.find(([name]) => name === "zip")?.[1] ?? "";
      const keptState = !!values.find(([name]) => name === "state")?.[1];
      const keptCity = !!values.find(([name]) => name === "city")?.[1];
      if (zip && keptState && keptCity) hydratedZip.current = zip;
      autosave.flush();
      updateScan("license", {
        kind: "success",
        msg: applied.length === 0
          ? SCAN_KEPT_MESSAGE
          : `Pre-filled ${scanLabels(applied).join(", ")} from your license — please review the fields below and fix anything that's off.`,
      });
    } catch (e) {
      updateScan("license", {
        kind: "error",
        msg: scanErrorMessage(e, "license", "fill in the fields below yourself"),
        retry: isRetryableScanError(e) ? prepared : null,
      });
    } finally {
      updateScan("license", { busy: false });
    }
  }

  async function pickCheck(f: File) {
    scanOnlyEmpty.current.check = !!previousFiles.voidedCheck && !checkOnFile.onFile;
    const prepared = await startScan("check", f, attachCheck);
    if (prepared) await readCheck(prepared);
  }

  async function readCheck(prepared: File) {
    updateScan("check", { busy: true, kind: "info", msg: null, retry: null });
    const sv = { shouldValidate: form.formState.isSubmitted, shouldDirty: true };
    try {
      const d = await publicPartnerForm.scanVoidedCheck(prepared);
      const values: ScannedField<Fields>[] = [
        ["bankRoutingNumber", formatRouting(d.routingNumber ?? ""), "routing number"],
        ["bankAccountNumber", digitsOnly(d.accountNumber ?? "", 17), "account number"],
      ];
      const { read, applied } = applyScan(form, values, { ...sv, onlyEmpty: scanOnlyEmpty.current.check });
      if (read.length === 0) {
        updateScan("check", {
          kind: "error",
          msg: "We couldn't read the routing and account numbers off that file. Please type them below.",
        });
        return;
      }
      autosave.flush();
      updateScan("check", {
        kind: "success",
        msg: applied.length === 0
          ? SCAN_KEPT_MESSAGE
          : `Pre-filled ${scanLabels(applied).join(" and ")} from your check — please check them against the paper copy.`,
      });
    } catch (e) {
      updateScan("check", {
        kind: "error",
        msg: scanErrorMessage(e, "check", "type the routing and account numbers below"),
        retry: isRetryableScanError(e) ? prepared : null,
      });
    } finally {
      updateScan("check", { busy: false });
    }
  }

  async function onValid(v: Fields) {
    setError(null);
    setFailedDocs([]);
    setMissingDocs([]);
    if (flagMissingDocs().length > 0) return;
    try {
      const [licData, chkData] = await Promise.all([
        license ? toBase64(license) : null,
        check ? toBase64(check) : null,
      ]);
      const r = await publicPartnerForm.submit({
        token,
        ...v,
        driverLicenseImageBase64: licData,
        driverLicenseFileName: license?.name ?? null,
        voidCheckImageBase64: chkData,
        voidCheckFileName: check?.name ?? null,
      });
      autosave.discard();
      setRestoredAt(null);
      setPreviousFiles({});
      setDone({ emailAlreadyInUse: !!r?.emailAlreadyInUse });
    } catch (e) {
      const slots = failedDocSlots(e);
      if (slots.length > 0) {
        setFailedDocs(slots);
        setFailAttempt((n) => n + 1);
        if (slots.includes("driverLicense")) setLicense(null);
        if (slots.includes("voidedCheck")) setCheck(null);
        return;
      }
      const missing = requiredDocSlots(e);
      if (missing.length > 0) {
        if (missing.includes("driverLicense")) {
          setLicense(null);
          setLicenseOnFile({ onFile: false, name: null });
        }
        if (missing.includes("voidedCheck")) {
          setCheck(null);
          setCheckOnFile({ onFile: false, name: null });
        }
        setMissingDocs(missing);
        scrollToSlot(missing[0] === "driverLicense" ? "license" : "check");
        return;
      }
      setError(e instanceof ApiError ? e.message : "Submission failed. Please try again.");
    }
  }

  function slotError(slot: DocSlot, file: File | null, onFile: DocOnFile): string | null {
    if (file) return null;
    if (failedDocs.includes(slot)) return REUPLOAD_HINT;
    if (missingDocs.includes(slot) && !onFile.onFile) {
      return slot === "driverLicense" ? MISSING_LICENSE : MISSING_CHECK;
    }
    return null;
  }

  function scrollToSlot(key: DocKey) {
    requestAnimationFrame(() => {
      const el = document.getElementById(`doc-slot-${key}`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.querySelector("label")?.focus({ preventScroll: true });
    });
  }

  function goToFailedDoc() {
    scrollToSlot(failedDocs.includes("driverLicense") ? "license" : "check");
  }

  function flagMissingDocs(): DocSlot[] {
    const missing: DocSlot[] = [
      ...(!license && !licenseOnFile.onFile ? ["driverLicense" as const] : []),
      ...(!check && !checkOnFile.onFile ? ["voidedCheck" as const] : []),
    ];
    setMissingDocs(missing);
    if (missing.length > 0) scrollToSlot(missing[0] === "driverLicense" ? "license" : "check");
    return missing;
  }

  function onInvalid() {
    if (flagMissingDocs().length > 0) return;
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>('[aria-invalid="true"]');
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus({ preventScroll: true });
    });
  }

  if (linkState === "checking") {
    return <Centered><Loading>Loading…</Loading></Centered>;
  }
  if (linkState !== "valid") {
    return (
      <Centered>
        <h1 className="text-xl font-semibold text-slate-800">
          {linkState === "expired" ? "Link expired" : "Link not valid"}
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          {linkState === "expired"
            ? "This partner form link has expired. Please ask your contact at BLogic for a new one."
            : "This partner form link is invalid. Please ask your contact at BLogic for a new one."}
        </p>
      </Centered>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen bg-white sm:bg-slate-50 flex flex-col">
        <Header />
        <main className="flex-1 sm:px-4 sm:py-10">
          <div className="max-w-2xl mx-auto bg-white sm:rounded-2xl sm:border sm:border-slate-200">
            <Confirmation emailAlreadyInUse={done.emailAlreadyInUse} />
          </div>
        </main>
      </div>
    );
  }

  const submitting = form.formState.isSubmitting;
  const scanning = scan.license.busy || scan.check.busy;
  const licenseError = slotError("driverLicense", license, licenseOnFile);
  const checkError = slotError("voidedCheck", check, checkOnFile);
  const conversionError = conversionMessage(failedDocs);

  return (
    <div className="min-h-screen bg-white sm:bg-slate-50 flex flex-col">
      <Header />
      <Form {...form}>
        <div className="mx-auto w-full max-w-2xl sm:mt-5 sm:mb-10">
        <div className="px-4 sm:px-0">
          <DraftSaveIndicator status={autosave.status} savedAt={autosave.savedAt} />
        </div>
        <form
          onSubmit={form.handleSubmit(onValid, onInvalid)}
          className="w-full bg-white px-4 py-6 sm:rounded-2xl sm:border sm:border-slate-200 sm:p-8 sm:shadow-sm"
        >
          {restoredAt != null && <DraftRestoredNotice at={restoredAt} onStartOver={startOver} disabled={scanning} />}

          <div className="mb-6">
            <h1 className="text-xl font-semibold text-slate-900">Partner Form</h1>
            <p className="mt-1 text-sm text-slate-500">
              Upload your ID and a voided check to get started.
            </p>
          </div>

          {prefilled && restoredAt == null && (
            <p className="mb-6 rounded-lg bg-sky-50 px-4 py-3 text-sm text-sky-900">
              We've filled in what we already have on file — please review each field and
              update anything that's changed.
            </p>
          )}

          <div className="space-y-5">
            <StepHeading
              title="Upload documents"
              subtitle="Upload or take a clear photo or PDF of each document. We'll scan them and use the information to prefill your form."
            />
            {conversionError && (
              <ConversionErrorBanner message={conversionError} attempt={failAttempt} onJump={goToFailedDoc} />
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
              <DocUpload slot="license" label="Driver's license" file={license} onFile={licenseOnFile} error={licenseError} previousNote={previousFileNote("driverLicense", previousFiles.driverLicense)} onPick={pickLicense} onRemove={() => removeDoc("license", attachLicense)} busy={scan.license.busy} />
              <DocUpload slot="check" label="Voided check" file={check} onFile={checkOnFile} error={checkError} previousNote={previousFileNote("voidedCheck", previousFiles.voidedCheck)} onPick={pickCheck} onRemove={() => removeDoc("check", attachCheck)} busy={scan.check.busy} />
            </div>
            <ScanFeedback label="Driver's license" state={scan.license} onRetry={retryScan(scan.license, readLicense)} />
            <ScanFeedback label="Voided check" state={scan.check} onRetry={retryScan(scan.check, readCheck)} />
          </div>

          <FormFieldSize value="lg">
            <FormRequiredCue>
              <div className="mt-8 space-y-5 border-t border-slate-200 pt-6">
                <StepHeading
                  title="Legal"
                  subtitle="Your name and business details, exactly as they appear on your income tax return."
                />

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <TextField control={form.control} name="firstName" label="First name" required placeholder="Jane" />
                  <TextField control={form.control} name="middleName" label="Middle name" placeholder="Quinn" />
                  <TextField control={form.control} name="lastName" label="Last name" required placeholder="Doe" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <DateField control={form.control} name="dateOfBirth" label="Date of birth" required disableFuture defaultYear={1990} />
                  <TextField control={form.control} name="phone" label="Phone" required inputMode="tel" placeholder="(555) 123-4567" format={formatPhone} />
                  <TextField control={form.control} name="email" label="Email" required inputMode="email" placeholder="you@business.com" />
                </div>

                <TextField control={form.control} name="addressLine1" label="Address" required placeholder="123 Main St" />

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <TextField control={form.control} name="zip" label="ZIP" required inputMode="numeric" maxLength={10} placeholder="94103" format={formatZip} />
                  <SelectField control={form.control} name="state" label="State" required placeholder="Select" options={STATE_OPTIONS} />
                  <TextField control={form.control} name="city" label="City" required placeholder="San Francisco" />
                </div>

                <div className="border-t border-slate-200 pt-6">
                  <StepHeading
                    title="IRS/1099"
                    subtitle="How your entity is structured and the tax ID we report earnings under."
                  />

                  <div className="space-y-5">
                    <SelectField
                      control={form.control}
                      name="ownershipType"
                      label="Entity type"
                      required
                      placeholder="Select"
                      options={ownershipOptions}
                    />

                    {isSoleProp ? (
                      <TextField control={form.control} name="ssn" label="Social Security Number (SSN)" required placeholder="XXX-XX-XXXX" inputMode="numeric" format={formatSsn} hint="9 digits — format 123-45-6789." />
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <TextField control={form.control} name="ein" label="Employer Identification Number (EIN)" required placeholder="XX-XXXXXXX" inputMode="numeric" format={formatEin} hint="9 digits — format 12-3456789." />
                        <TextField control={form.control} name="legalName" label="Legal Business Name" required placeholder="Acme Coffee Co." />
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <TextField control={form.control} name="bankRoutingNumber" label="Routing Number" required placeholder="021000021" inputMode="numeric" format={formatRouting} />
                      <TextField control={form.control} name="bankAccountNumber" label="Bank Account Number" required placeholder="Account number" inputMode="numeric" format={(v) => digitsOnly(v, 17)} />
                    </div>
                  </div>
                </div>
              </div>
            </FormRequiredCue>
          </FormFieldSize>

          {error && <p className="mt-5 text-sm text-destructive">{error}</p>}

          <Button type="submit" disabled={submitting || scanning} size="lg" className="mt-6 w-full">
            {submitting && <Spinner />}
            {submitting ? "Submitting…" : "Submit"}
          </Button>
        </form>
        </div>
      </Form>
    </div>
  );
}

function retryScan(state: ScanState, read: (file: File) => Promise<void>): (() => void) | undefined {
  const file = state.retry;
  return file ? () => { void read(file); } : undefined;
}

function ScanFeedback({ label, state, onRetry }: { label: string; state: ScanState; onRetry?: () => void }) {
  if (state.busy) {
    return (
      <div className="rounded-xl border border-primary bg-primary/10 px-4 py-3">
        <BusyIndicator label={`Reading your ${label.toLowerCase()}…`} />
      </div>
    );
  }
  if (!state.msg) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5">
      <span className="text-xs font-semibold text-slate-700">{label}</span>
      <ScanStatus msg={state.msg} kind={state.kind} onRetry={onRetry} />
    </div>
  );
}

function DocUpload({
  slot, label, file, onFile, error, previousNote, onPick, onRemove, busy,
}: {
  slot: DocKey;
  label: string;
  file: File | null;
  onFile: DocOnFile;
  error?: string | null;
  previousNote?: string | null;
  onPick: (f: File) => void;
  onRemove: () => void;
  busy?: boolean;
}) {
  const drop = useFileDrop({ accept: DOC_ACCEPT, busy, onFile: (f) => onPick(withDocMimeType(f)) });
  const satisfied = !!file || onFile.onFile;
  const preview = useFilePreview(file);
  const message = drop.rejection ?? error ?? null;
  const reattach = !satisfied && !message && !!previousNote;
  const blocked = drop.phase === "blocked";
  const removable = !!file && !busy;

  function remove() {
    drop.clearRejection();
    onRemove();
  }

  return (
    <div id={`doc-slot-${slot}`}>
      <div className="relative" {...drop.zoneProps}>
        <label
          tabIndex={-1}
          aria-busy={busy}
          className={cn(
            "flex items-start gap-3 rounded-xl border-2 p-4 transition-colors [&_*]:pointer-events-none",
            !busy && !blocked && "cursor-pointer",
            removable && "pr-11",
            fileDropTone({ phase: drop.phase, busy, invalid: !!message, filled: satisfied, attention: reattach }),
          )}
        >
          <span className={`mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg ${satisfied && !blocked ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
            {blocked ? (
              <Ban className="size-4" aria-hidden="true" />
            ) : preview ? (
              <img src={preview} alt={`Preview of ${file?.name ?? label}`} className="size-9 rounded-lg object-cover" />
            ) : (
              <Camera className="size-4" aria-hidden="true" />
            )}
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-medium text-slate-700">
              {label}{" "}
              {file ? (
                <span className="text-emerald-700">✓ added</span>
              ) : onFile.onFile ? (
                <span className="text-emerald-700">✓ on file</span>
              ) : (
                <span className="text-destructive" aria-label="required">*</span>
              )}
            </span>
            <span className="block truncate text-xs text-slate-500">
              {blocked ? (
                "This file type isn't supported"
              ) : file ? (
                file.name
              ) : onFile.onFile ? (
                `${onFile.name ?? "Already uploaded"} — tap to replace`
              ) : (
                <>
                  <span className="sm:hidden">Tap to take a photo or choose a file</span>
                  <span className="hidden sm:inline">
                    Drag and drop a file here, or <span className="font-medium text-primary">browse</span>
                  </span>
                </>
              )}
            </span>
          </span>
          <input {...drop.inputProps} />
        </label>
        {removable && (
          <button
            type="button"
            onClick={remove}
            aria-label={`Remove ${label}`}
            title="Remove"
            className="absolute right-2 top-2 rounded-md p-1.5 text-slate-500 transition-colors hover:bg-white hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        )}
      </div>
      {message && (
        <p className="mt-1.5 text-xs font-medium text-destructive" role="alert">{message}</p>
      )}
      {reattach && (
        <p className="mt-1.5 text-xs text-amber-700">{previousNote}</p>
      )}
    </div>
  );
}

function Header() {
  return (
    <header className="bg-white border-b border-slate-200">
      <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center font-bold text-primary-foreground text-sm">
          BL
        </div>
        <div className="leading-tight">
          <div className="font-semibold text-slate-900 text-sm">BLogic Systems</div>
          <div className="text-[11px] text-slate-500">Partner Onboarding</div>
        </div>
      </div>
    </header>
  );
}

function Confirmation({ emailAlreadyInUse }: { emailAlreadyInUse: boolean }) {
  return (
    <div className="text-center py-12 px-6">
      <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-2xl font-semibold">
        ✓
      </div>
      <h1 className="text-xl font-semibold text-slate-800 mt-4">Thank You</h1>
      <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
        Your information has been submitted. A BLogic representative will be in
        touch if anything else is needed.
      </p>
      {emailAlreadyInUse && (
        <p className="text-sm text-amber-700 mt-4 max-w-md mx-auto">
          The email you entered is already used by another account, so we kept the one
          already on file. A representative will follow up about it.
        </p>
      )}
    </div>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white sm:bg-slate-50 sm:px-4">
      <div className="w-full max-w-md bg-white p-8 text-center sm:rounded-xl sm:border sm:border-slate-200 sm:shadow-sm">
        {children}
      </div>
    </div>
  );
}

function firstFilled(...values: Array<string | null | undefined>): string {
  for (const v of values) {
    const s = (v ?? "").trim();
    if (s) return s;
  }
  return "";
}

function masked(raw: string, format: (v: string) => string): string {
  if (!raw || /[^\d\s\-()+.]/.test(raw)) return "";
  return format(raw);
}

function splitFullName(full: string): [string, string] {
  const parts = full.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return [parts[0] ?? "", ""];
  return [parts.slice(0, -1).join(" "), parts[parts.length - 1]];
}

function toFields(p: PartnerFormPrefill): Fields {
  const party = p.party;
  const f = party ? null : p.currentForm;
  const [fallbackFirst, fallbackLast] = splitFullName(
    firstFilled(party?.fullName, p.user?.name),
  );
  return {
    firstName: firstFilled(party?.firstName, f?.firstName, fallbackFirst),
    middleName: firstFilled(party?.middleName, f?.middleName),
    lastName: firstFilled(party?.lastName, f?.lastName, fallbackLast),
    dateOfBirth: toDateInput(firstFilled(party?.dateOfBirth, f?.dateOfBirth)),
    ownershipType: canonicalOwnershipType(firstFilled(party?.ownershipType, f?.ownershipType)),
    ssn: masked(firstFilled(party?.ssn, f?.ssn), formatSsn),
    ein: masked(firstFilled(party?.ein, f?.ein), formatEin),
    legalName: firstFilled(party?.legalName, f?.legalName),
    email: firstFilled(party?.email, f?.email, p.user?.email),
    phone: masked(firstFilled(party?.phone, f?.businessPhone), formatPhone),
    addressLine1: firstFilled(party?.addressLine1, f?.addressLine1),
    zip: masked(firstFilled(party?.zip, f?.zip), formatZip),
    state: stateCode(firstFilled(party?.state, f?.state)),
    city: firstFilled(party?.city, f?.city),
    bankRoutingNumber: masked(firstFilled(party?.bankRoutingNumber), formatRouting),
    bankAccountNumber: masked(firstFilled(party?.bankAccountNumber), (v) => digitsOnly(v, 17)),
  };
}

function hasAnyValue(v: Fields): boolean {
  return Object.values(v).some((s) => s !== "");
}

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result);
      const comma = s.indexOf(",");
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
